import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, Save, Send, Check, Loader2, Info, Download } from 'lucide-react';
import { getDailyActivity, saveDailyActivity, sendDailyReport } from '../utils/api';
import AgentLayout from '../components/AgentLayout';
import ReportInfoBlock from '../components/ReportInfoBlock';
import { toast } from 'sonner';

const COLUMNS = [
  { key: 'clientName', label: "Client's Name", type: 'text', wide: true },
  { key: 'leadType', label: 'Lead Type', type: 'text' },
  { key: 'contactMade', label: 'Contact Made?', type: 'select', options: ['', 'Yes', 'No'] },
  { key: 'presentationMade', label: 'Presentation Made?', type: 'select', options: ['', 'Yes', 'No'] },
  { key: 'objections', label: 'Objections?', type: 'text' },
  { key: 'applicationsWritten', label: '# of Applications Written', type: 'number' },
  { key: 'monthlyPremium', label: 'Monthly Premium', type: 'currency' },
  { key: 'annualizedPremium', label: 'Annualized Premium', type: 'currency' },
  { key: 'referralsReceived', label: '# of Referrals Received', type: 'number' },
];

const DailyActivity = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [entries, setEntries] = useState([]);
  const [reportMeta, setReportMeta] = useState({ dailyApGoal: '', areasWorked: '', managerTrainer: 'Fred Hawkins' });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const autosaveTimer = useRef(null);
  const agentName = JSON.parse(localStorage.getItem('agentInfo') || '{}').fullName || 'Agent';

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    if (!token) { navigate('/agent/login'); return; }
    loadDay(currentDate);
  }, [currentDate]);

  const loadDay = async (date) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('agentToken');
      const result = await getDailyActivity(date, token);
      if (result.success) {
        setEntries(result.activity.entries || createBlankEntries());
        setReportMeta(result.activity.reportMeta || { dailyApGoal: '', areasWorked: '', managerTrainer: 'Fred Hawkins' });
        setIsSent(false);
      }
    } catch (e) {
      console.error('Error loading daily activity:', e);
      setEntries(createBlankEntries());
    } finally {
      setIsLoading(false);
    }
  };

  const createBlankEntries = () =>
    Array.from({ length: 12 }, (_, i) => ({
      row: i + 1, clientName: '', leadType: '', contactMade: '', presentationMade: '',
      objections: '', applicationsWritten: 0, monthlyPremium: 0, annualizedPremium: 0, referralsReceived: 0,
    }));

  const updateEntry = (rowIndex, field, value) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [field]: value };
      return updated;
    });
    scheduleAutosave();
  };

  const updateMeta = (field, value) => {
    setReportMeta(prev => ({ ...prev, [field]: value }));
    scheduleAutosave();
  };

  const scheduleAutosave = useCallback(() => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => doAutosave(), 2000);
  }, [currentDate]);

  const doAutosave = async () => {
    try {
      const token = localStorage.getItem('agentToken');
      await saveDailyActivity({ date: currentDate, entries, reportMeta, status: 'draft' }, token);
    } catch (e) { /* silent autosave */ }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('agentToken');
      await saveDailyActivity({ date: currentDate, entries, reportMeta, status: 'draft' }, token);
      toast.success('Daily activity saved!');
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSend = async () => {
    setIsSending(true);
    try {
      const token = localStorage.getItem('agentToken');
      await saveDailyActivity({ date: currentDate, entries, reportMeta, status: 'submitted' }, token);
      const dayName = new Date(currentDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
      const dayData = {
        dayOfWeek: dayName,
        date: currentDate,
        entries,
        reportMeta,
        totals: calculateTotals(),
      };
      const result = await sendDailyReport({ weekStart: currentDate, dayData }, token);
      if (result.success) {
        toast.success('Daily report sent to your trainer!');
        setIsSent(true);
      } else {
        toast.error(result.message || 'Failed to send');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send daily report');
    } finally {
      setIsSending(false);
    }
  };

  const calculateTotals = () => ({
    applicationsWritten: entries.reduce((s, e) => s + (e.applicationsWritten || 0), 0),
    monthlyPremium: entries.reduce((s, e) => s + (e.monthlyPremium || 0), 0),
    annualizedPremium: entries.reduce((s, e) => s + (e.annualizedPremium || 0), 0),
    referralsReceived: entries.reduce((s, e) => s + (e.referralsReceived || 0), 0),
  });

  const navigateDay = (dir) => {
    const d = new Date(currentDate + 'T12:00:00');
    d.setDate(d.getDate() + dir);
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  const totals = calculateTotals();
  const formattedDate = new Date(currentDate + 'T12:00:00').toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
  const hasData = entries.some(e => e.clientName || e.applicationsWritten > 0 || e.monthlyPremium > 0 || e.annualizedPremium > 0);

  if (isLoading) {
    return (
      <AgentLayout title="Daily Activity Sheet">
        <div className="flex items-center justify-center h-48">
          <Loader2 className="animate-spin text-brand-blue" size={32} />
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Daily Activity Sheet">
      <div className="space-y-3" data-testid="daily-activity-page">
        {/* Header Row: Date Nav + Info Block + Reminders */}
        <div className="flex gap-3 items-start flex-wrap">
          {/* Date Navigation */}
          <div className="bg-white rounded-lg shadow-md p-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              <button onClick={() => navigateDay(-1)} className="p-1.5 rounded-lg hover:bg-gray-100 transition" data-testid="prev-day-btn">
                <ChevronLeft size={18} />
              </button>
              <div className="text-center min-w-[180px]">
                <p className="text-sm font-bold text-gray-900">{formattedDate}</p>
                <input
                  type="date"
                  value={currentDate}
                  onChange={(e) => setCurrentDate(e.target.value)}
                  className="mt-1 text-xs text-brand-blue cursor-pointer border rounded px-2 py-0.5"
                  data-testid="date-picker"
                />
              </div>
              <button onClick={() => navigateDay(1)} className="p-1.5 rounded-lg hover:bg-gray-100 transition" data-testid="next-day-btn">
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          {/* Info Block */}
          <ReportInfoBlock
            agentName={agentName}
            dateLabel="Today's Date"
            dateValue={formattedDate}
            goalLabel="Daily AP Goal"
            goalValue={reportMeta.dailyApGoal}
            onGoalChange={(v) => updateMeta('dailyApGoal', v)}
            areasWorked={reportMeta.areasWorked}
            onAreasWorkedChange={(v) => updateMeta('areasWorked', v)}
          />

          {/* Reminders */}
          <div className="hidden lg:flex flex-col bg-white rounded-lg shadow-md p-4 min-w-[260px] ml-auto" data-testid="daily-reminder-box">
            <h3 className="text-sm font-bold text-brand-blue mb-3 flex items-center gap-1.5">
              <Info size={14} /> Reminders
            </h3>
            <ul className="space-y-2.5 text-xs text-gray-600">
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                <span>Data <strong>auto-saves</strong> as you type</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-orange mt-1.5 flex-shrink-0" />
                <span>Click <strong>Send Report</strong> to email your daily activity to your trainer</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-brand-blue mt-1.5 flex-shrink-0" />
                <span>Document and review with your Manager daily</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Daily Activity Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="daily-activity-table-container">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="daily-activity-table">
              <thead>
                <tr className="bg-brand-blue text-white">
                  <th className="px-1 py-2 text-center font-bold text-xs w-8">#</th>
                  {COLUMNS.map(col => (
                    <th key={col.key} className={`px-1 py-2 text-center font-bold text-xs ${col.wide ? 'min-w-[160px]' : 'min-w-[100px]'}`}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, i) => (
                  <tr key={i} className="border-b hover:bg-blue-50/30" data-testid={`daily-row-${i}`}>
                    <td className="px-1 py-1 text-center text-xs font-semibold text-gray-400">{i + 1}</td>
                    {COLUMNS.map(col => (
                      <td key={col.key} className="px-1 py-1 text-center">
                        {col.type === 'select' ? (
                          <select
                            value={entry[col.key]}
                            onChange={(e) => updateEntry(i, col.key, e.target.value)}
                            className="w-full px-1 py-1 border rounded text-xs text-center"
                            data-testid={`daily-${col.key}-${i}`}
                          >
                            {col.options.map(opt => (
                              <option key={opt} value={opt}>{opt || '-'}</option>
                            ))}
                          </select>
                        ) : col.type === 'number' || col.type === 'currency' ? (
                          <input
                            type="number"
                            step={col.type === 'currency' ? '0.01' : '1'}
                            value={entry[col.key]}
                            onChange={(e) => updateEntry(i, col.key, col.type === 'currency' ? (parseFloat(e.target.value) || 0) : (parseInt(e.target.value) || 0))}
                            className={`px-1 py-1 border rounded text-center text-xs ${col.type === 'currency' ? 'w-20' : 'w-14'}`}
                            min="0"
                            data-testid={`daily-${col.key}-${i}`}
                          />
                        ) : (
                          <input
                            type="text"
                            value={entry[col.key]}
                            onChange={(e) => updateEntry(i, col.key, e.target.value)}
                            className={`px-1 py-1 border rounded text-xs ${col.wide ? 'w-full min-w-[140px]' : 'w-full min-w-[80px]'}`}
                            data-testid={`daily-${col.key}-${i}`}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-brand-blue text-white font-bold" data-testid="daily-totals-row">
                  <td className="px-1 py-2 text-center text-xs" colSpan={6}>TOTALS</td>
                  <td className="px-1 py-2 text-center text-xs">{totals.applicationsWritten}</td>
                  <td className="px-1 py-2 text-center text-xs">${totals.monthlyPremium.toFixed(2)}</td>
                  <td className="px-1 py-2 text-center text-xs">${totals.annualizedPremium.toFixed(2)}</td>
                  <td className="px-1 py-2 text-center text-xs">{totals.referralsReceived}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 border-t text-xs text-gray-500 italic text-center">
            Please Document and Review everyday
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-5 py-2.5 bg-brand-blue text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition disabled:opacity-50"
            data-testid="save-daily-btn"
          >
            {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {isSaving ? 'Saving...' : 'Save Report'}
          </button>

          {hasData && (
            isSent ? (
              <span className="flex items-center gap-1.5 text-green-600 font-semibold text-sm" data-testid="daily-sent-badge">
                <Check size={16} /> Report Sent
              </span>
            ) : (
              <button
                onClick={handleSend}
                disabled={isSending}
                className="flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white rounded-lg text-sm font-semibold hover:bg-orange-600 transition disabled:opacity-50"
                data-testid="send-daily-btn"
              >
                {isSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {isSending ? 'Sending...' : 'Send Report'}
              </button>
            )
          )}
        </div>
      </div>
    </AgentLayout>
  );
};

export default DailyActivity;
